import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { createSubscriber } from "../../../controllers/subscribersController";
import { getCasinoFeedbacks } from "../../../controllers/homeController";
import avatar1 from "../../../assets/aboutus.png";
import { Star } from "lucide-react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";

import "swiper/css";
import "swiper/css/autoplay";
import "swiper/css/bundle";
const NewsletterTestimonials = () => {
  const [testimonials, setTestimonials] = useState([]);

  // 🔹 New states for subscription logic
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [apiError, setApiError] = useState("");

  // 🔹 Handle subscription form
  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");
    setIsSuccess(false);

    if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
      setApiError("Please enter a valid email address.");
      return;
    }

    setIsSubmitting(true);
    try {
      await createSubscriber(email, "N/A");
      setIsSuccess(true);
      setEmail("");
    } catch (err) {
      setApiError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTestimonials = async () => {
    const tt = (await getCasinoFeedbacks()) || [];
    const data = (Array.isArray(tt) ? tt : []).map((t) => ({
      id: t.id ?? "",
      name: t.name ?? "",
      quote: t.review ?? "",
      rating: t.rating ?? "",
      title: t.title ?? "",
      avatarUrl: t.imageUrl ?? null,
    }));

    setTestimonials(data);
  };

  useEffect(() => {
    getTestimonials();
  }, []);
  return (
    <section className="bg-white dark:bg-darkbg text-textLight dark:text-textDark transition-colors duration-300">
      {/* Newsletter */}
      <div className="w-full mx-auto max-w-[1200px] 2xl:max-w-[1300px] px-6 xl:px-0 pt-16 sm:pt-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 items-center gap-12">
          {/* Text Section */}
          <div className="text-left lg:pl-4">
            <motion.p
              className="text-textLight/70 dark:text-textDark/70 text-sm font-semibold mb-3"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              Newsletter Subscription Section
            </motion.p>

            <motion.h2
              className="font-extrabold tracking-tight text-[28px] leading-[1.2] sm:text-[32px] mb-3 dark:text-textDark"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              Join our newsletter today and be
              <br className="hidden sm:block" />
              the first to discover exciting new
              <br className="hidden sm:block" />
              casinos and exclusive
              <br className="hidden sm:block" />
              promotions!
            </motion.h2>

            <motion.p
              className="text-textLight/80 dark:text-textDark/80 text-[14px] leading-relaxed mb-5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7 }}
            >
              Subscribe now to receive personalized offers and the latest
              updates on new casinos. Don’t miss out on the best deals—act fast
              and secure your spot!
            </motion.p>

            {/* 🔹 Form with success + error states */}
            <motion.form
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row items-center gap-2"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <input
                type="email"
                required
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-9 w-[240px] sm:w-[280px] rounded-md border border-border dark:border-borderDark bg-cardLight dark:bg-cardDark px-3 text-sm text-textLight dark:text-textDark placeholder:text-textLight/60 dark:placeholder:text-textDark/60 outline-none focus:ring-2 focus:ring-primary/30 transition-colors duration-300"
                autoComplete="email"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className={`h-9 rounded-md bg-primary px-4 text-white text-sm font-semibold hover:bg-primary-hover transition ${
                  isSubmitting ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {isSubmitting ? "Subscribing..." : "Subscribe"}
              </button>
            </motion.form>

            {/* 🔹 Error message */}
            {apiError && (
              <motion.p
                className="text-red-600 dark:text-red-400 text-sm mt-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {apiError}
              </motion.p>
            )}

            {/* 🔹 Success message */}
            {isSuccess && (
              <motion.p
                className="text-green-600 dark:text-green-400 text-sm mt-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                🎉 You’re in! Thanks for subscribing. Check your inbox for the
                latest updates and offers.
              </motion.p>
            )}
          </div>
        </div>
      </div>

      {/* Spacer */}
      <div className="h-20 sm:h-24" />

      {/* Testimonials heading */}
      <div className="w-full mx-auto max-w-[1200px] 2xl:max-w-[1300px] px-6 xl:px-0">
        <div className="text-center max-w-[780px] mx-auto">
          <motion.p
            className="text-textLight/70 dark:text-textDark/70 text-xs font-semibold mb-3"
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            User Testimonials
          </motion.p>
          <motion.h3
            className="font-extrabold tracking-tight text-[24px] sm:text-[28px] leading-[1.2] mb-3 dark:text-textDark"
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            Trusted Experiences: Real
            <br className="hidden sm:block" />
            Results from Our Users
          </motion.h3>
          <motion.p
            className="text-textLight/70 dark:text-textDark/70 text-sm leading-relaxed mb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            Discover how our platform has empowered users to confidently
            navigate the online casino landscape. With verified reviews and
            comparisons, our users have achieved remarkable wins and found their
            perfect match.
          </motion.p>
        </div>
      </div>

      {/* Testimonials cards */}
      <div className="w-full mx-auto max-w-[1200px] 2xl:max-w-[1300px] px-0 xl:px-0 pb-16 sm:pb-24">
        <div className="">
          <Swiper
            modules={[Autoplay]}
            spaceBetween={50}
            slidesPerView={3}
            autoplay={{ delay: 3000 }}
            loop={true}
          >
            {testimonials.map((t, idx) => (
              <SwiperSlide key={t.id}>
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.25 }}
                  transition={{ duration: 0.35, delay: idx * 0.06 }}
                  className="bg-cardLight dark:bg-cardDark border border-border dark:border-borderDark rounded-xl shadow-sm w-full transition-colors duration-300"
                >
                  <div className={`p-6 ${idx === 2 ? "pb-3" : ""}`}>
                    <p
                      className="text-sm leading-relaxed mb-4 text-textLight dark:text-textDark"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      “{t.quote}”
                    </p>

                    <div className="flex items-center gap-3">
                      <img
                        src={t.avatarUrl || avatar1}
                        alt={t.name || "User"}
                        className="w-9 h-9 rounded-full object-cover"
                      />
                      <div>
                        <div className="text-sm font-semibold text-textLight dark:text-textDark">
                          {t.name}
                        </div>
                        <div className="text-[12px] text-textLight/70 dark:text-textDark/70">
                          {t.title}
                        </div>
                        <div className="flex items-center mt-1">
                          {[...Array(5)].map((_, starIdx) => (
                            <Star
                              key={starIdx}
                              className="text-yellow-400 w-5"
                              fill="currentColor"
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
};

export default NewsletterTestimonials;
